import { defineStore, getActivePinia, acceptHMRUpdate } from 'pinia';
import { ITEM_LOCATION_TYPES, ROADMAP_REQUEST_FIELDS } from '@/roadmaps/constants/roadmaps.constants';
import useScroll from '@/roadmaps/use/use-scroll';
import * as api from '@/stores/api/roadmaps.api';
import { asyncQueue } from '@/stores/helpers/roadmaps-async-queue.helper';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';

interface Swimlane {
    id: string;
    index: number;
    action?: string;
    [key: string]: unknown;
}

interface SwimlanesState {
    swimlanes: Record<string, Swimlane>;
}

export function getInitialState (): SwimlanesState {
    return {
        swimlanes: {},
    };
}

export const useRoadmapsSwimlaneStore = defineStore('roadmaps/swimlanes', {
    state: getInitialState,
    getters: {
        swimlanes (): Record<string, Swimlane> {
            const rawState = getActivePinia()!.state.value['roadmaps/swimlanes'] as SwimlanesState;

            return {
                ...rawState.swimlanes,
                ...(asyncQueue.queuedUpdates.swimlanes as Record<string, Swimlane>),
            };
        },
        orderedSwimlanes (): Swimlane[] {
            return Object.values(this.swimlanes).sort((a, b) => a.index - b.index);
        },
        swimlaneById () {
            return (swimlaneId: string): Swimlane | undefined => this.swimlanes[swimlaneId];
        },
        swimlaneByItemId () {
            return (itemId: string): Swimlane | null => {
                // eslint-disable-next-line @typescript-eslint/no-require-imports -- circular dep; runtime require breaks init cycle
                const { useRoadmapsItemsStore } = require('@/stores/roadmaps-items.store');
                const itemsStore = useRoadmapsItemsStore();
                const item = itemsStore.itemById(itemId);
                if (!item) return null;
                if (item.parentType === '') return null;
                if (item.parentType === ITEM_LOCATION_TYPES.SWIMLANE.key) {
                    return this.swimlanes[item.parentId] ?? null;
                }

                return this.swimlaneByItemId(item.parentId);
            };
        },
        swimlaneIds (): string[] {
            // reads raw state intentionally — layout calculations must not see optimistically-queued swimlanes
            const rawState = getActivePinia()!.state.value['roadmaps/swimlanes'] as SwimlanesState;

            return Object.keys(rawState.swimlanes);
        },
        swimlaneByIndex () {
            return (index: number): Swimlane | undefined => this.orderedSwimlanes[index];
        },
        firstSwimlaneId (): string | undefined {
            return this.orderedSwimlanes[0]?.id;
        },
        emptyRowsInSwimlane () {
            return (swimlaneId: string): number[] => {
                // eslint-disable-next-line @typescript-eslint/no-require-imports -- circular dep; runtime require breaks init cycle
                const { useRoadmapsItemsStore } = require('@/stores/roadmaps-items.store');
                const itemsStore = useRoadmapsItemsStore();
                const itemsInSwimlane = itemsStore.itemsByParentId(swimlaneId);
                if (itemsInSwimlane.length === 0) return [];

                const rowsWithItems = new Set<number>(itemsInSwimlane.map((item: { row: number }) => item.row));
                const maxRow = Math.max(...rowsWithItems) + 1;
                const allRows = Array.from({ length: maxRow }, (_value, index) => index);

                return allRows.filter((row) => !rowsWithItems.has(row));
            };
        },
    },
    actions: {
        setMap ({ swimlanes }: { swimlanes: Record<string, unknown> }) {
            this.$patch((state) => {
                state.swimlanes = mapValues(swimlanes, (swimlane) => Object.freeze(swimlane)) as Record<string, Swimlane>;
            });
        },
        async updateSwimlanes (swimlanesToUpdate: Swimlane[]): Promise<unknown> {
            const task = async () => {
                const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
                const roadmapsStore = useRoadmapsStore();
                const { currentRoadmap } = roadmapsStore;
                const payload = {
                    swimlanes: map(swimlanesToUpdate, (item) => {
                        return pick(item, ROADMAP_REQUEST_FIELDS.swimlane);
                    }),
                    roadmapVersionTimestamp: currentRoadmap?.lastUpdatedAt,
                };
                const updatedRoadmap = await api.updateSwimlanes(currentRoadmap!.id, payload);
                await roadmapsStore.setOne(updatedRoadmap);

                const { resetHorizontalScrollSyncComponents } = useScroll();
                resetHorizontalScrollSyncComponents();

                return updatedRoadmap.swimlanes;
            };

            const swimlanesWithoutDeleted = filter(swimlanesToUpdate, (swimlane) => swimlane?.action !== 'delete');

            return asyncQueue.enqueue({
                task,
                updates: keyBy(swimlanesWithoutDeleted, 'id'),
                updateType: 'swimlanes',
            });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useRoadmapsSwimlaneStore, module.hot));
}
